import {AppInitializeService} from '../app-initialize-service';
import {inject, Injectable} from '../di';
import {I18nService} from '../i18n/i18n-service';
import {Logger} from '../logger';
import {ConfigService} from '../config/config-service';
import {TimeService} from '../time-service';
import {TemplatesService} from './templates-service';
import {LoaderService} from '../loader/loader-service';
import {MediaService} from '../media-service';
import {ScreenSizeService} from '../screen-size-service';
import accounting from 'accounting';
import {UrlsService} from '../urls-service';
import {StructService} from '../struct/struct-service';
import {LegacyLogService} from '../logger/legacy-log-service';
import type {SvgModule} from '../../modules/svg/svg.module';
import {post} from '../utils/post';

@Injectable()
export class UiService implements AppInitializeService {
  private readonly logger = inject(Logger);
  private readonly i18nService = inject(I18nService);
  private readonly configService = inject(ConfigService);
  private readonly timeService = inject(TimeService);
  private readonly templatesService = inject(TemplatesService);
  private readonly loaderService = inject(LoaderService);
  private readonly mediaService = inject(MediaService);
  private readonly screenSizeService = inject(ScreenSizeService);
  private readonly urlsService = inject(UrlsService);
  private readonly structService = inject(StructService);
  private readonly legacyLogService = inject(LegacyLogService);

  /**
   * Is pages pre-rendering is in progress
   */
  private buffering: boolean = false;

  /**
   * Buffered pages html to render on page
   */
  private buffer: string = '';

  private _pages: Record<string, UiPageRegistryEntry> = {};

  constructor() {
    window.UI = this;
    window.renderPageOnTheStructv2 = this.renderPageOnTheStructv2.bind(this);
    window.loadAndRenderPageOnTheStructv2 = this.loadAndRenderPageOnTheStructv2.bind(this);
  }

  async init(): Promise<boolean> {
    try {
      const start = this.timeService.now;

      $('#tv_cur').hide();

      this.renderFullScreenOverlay();

      // TODO move functions there!
      $.views.helpers({
        getGrandParentPage: window.getGrandParentPage,
        getParentPage: window.getParentPage,
        getGrandChildren: window.getGrandChildren,
        getBreadcrumbId: window.getBreadcrumbId,
        getSiblings: window.getSiblings,
        getPageSiblings: window.getPageSiblings,
        isBreadcrumbShow: window.isBreadcrumbShow,
        formatMoney: (cost: string | number | null | undefined) =>
          cost != null ? accounting.formatMoney(cost, window.currency_format) : '',
        getlang: this.i18nService.translate.bind(this.i18nService),
      });

      this.bufferingStart();

      this.preRenderPages();

      this.bufferingStop();

      const end = this.timeService.now;
      const msg = `Rendered struct in ${end.diff(start)} ms`;
      this.legacyLogService.add(msg);
      this.logger.debug(`[UiService] ${msg}`);

      HotezaTV.metrics.system = this.timeService.uptime;
    } catch (error) {
      this.logger.critical('[UiService] Unable to pre-render pages', error);
      return false;
    }

    return true;
  }

  public hasPage(page: string) {
    return Object.keys(this._pages).includes(page);
  }

  //TODO: register_block: name, sel_list, keydown (keys) (metro_move)
  //TODO: dynamic page? with parameters
  /**
   * Register page
   */
  public register_page<T = any>(params: {
    id: string;
    action?: ((pageId: string) => void) | {func: (param: T) => void; param: T};
    images?: string[];
  }): boolean {
    if (this.hasPage(params.id)) {
      this.legacyLogService.add('UI: can`t register existing page');
      this.logger.warn(`[UiService] Unable to register page. Page with id: ${params.id} already registered`);
      return false;
    }

    this._pages[params.id] = {};
    if (params.action) {
      if (typeof params.action === 'function') {
        this._pages[params.id].action = {func: params.action};
      } else if (typeof params.action === 'object' && params.action.func != null && params.action.param != null) {
        this._pages[params.id].action = params.action;
      } else {
        //ERROR
        this.logger.error(
          `[UiService] Error in function "registerPage" parameter: Unknown signature of parameter "params.action". The page will be created without "action" field.`,
        );
      }
    }

    if (params.images && this.configService.config.preload_images == 3) {
      this._pages[params.id].images = params.images
        .filter((image) => !!image) // remove empty
        .filter((image, index, self) => self.indexOf(image) === index); // remove duplicates
    }

    return true;
  }

  public async navigate(pageId: string): Promise<void> {
    if (!this.hasPage(pageId)) {
      const msg = `Unable to navigate to "${pageId}" page: page was not registered`;
      this.logger.warn(`[UiService] ${msg}`);
      return Promise.reject(new Error(msg));
    }
    this.logger.debug(`[UiService] Navigating to ${pageId}...`);

    const config = this.configService.config;
    const page = this._pages[pageId];
    const images = page.images || [];

    if (config.virtual_render && !document.getElementById(pageId) && page.content) {
      $('#container').append(page.content);
    }

    if (images.length) {
      this.loaderService.start(true);
    }

    // Stop media from previous page
    if (this.mediaService.type != null) {
      await this.mediaService.stop({directType: null});
    }

    if (images.length) {
      await new window._PreloadMedia(images);
      this.loaderService.stop();
    }

    // todo legacy
    window.pref_log('preload');

    // TODO: переделать замену как-то нормально
    for (const image of images) {
      // Search for the image on that page
      const node = $(`#${pageId}`).find(`[new_image_url="${image}"]`)[0];
      if (node) {
        if (node.nodeName.toLowerCase() == 'img') {
          (node as HTMLImageElement).src = image;
        } else {
          // особенно вот это переделать
          node.style.cssText = node.style.cssText.replace(
            'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=',
            image,
          );
        }
        node.removeAttribute('new_image_url');
      }
    }

    delete page.images;

    // todo legacy
    window.pref_log('replace');

    if (page.action && page.action.func) {
      try {
        page.action.func(page.action.param != null ? page.action.param : pageId);
      } catch (err) {
        this.logger.error(`[] Unexpected error during ${pageId} action execution`, err);
      }
    }
  }

  public build_page(obj: {
    id: string | null;
    title: string;
    content: string;
    className?: string;
    back?: {
      href: string;
      onvclick: string;
    };
    class?: string;

    tpl?: boolean;
    devices?: string | null;
    groups?: string | null;
    tplType?: string;
    onopen?: string;
    onclose?: string;
    to?: string | Element;
    virtual_render?: boolean;
    width?: number;
    height?: number;
  }): string | undefined {
    // NOTE: legacy. Function has several call signatures, and can return html, HTML element id or undefined.

    // UI.build_page вызван с шаблоном
    if (obj.tpl) {
      const wrap = document.getElementById(obj.id);

      if (wrap) {
        wrap.innerHTML = obj.content;

        if (obj['class'] != undefined) {
          wrap.className = 'page ' + obj['class'];
        }
      } else {
        // если страницы нет, создаем ее по дефолтному шаблону

        let html: string = '';

        const data: {
          id: string | null;
          content: string;
          devices?: string | null;
          groups?: string | null;
          tplType?: string;
          onopen?: string;
          onclose?: string;
          wh: number;
          klass?: string;
        } = {
          content: obj.content,
          id: obj.id,
          devices: obj.devices,
          groups: obj.groups,
          tplType: obj.tplType,
          onopen: obj.onopen,
          onclose: obj.onclose,
          wh: this.screenSizeService.screenSize.wh,
        };

        if (obj['class'] != undefined) {
          data.klass = obj['class'];
        }

        // построение виджета в меню
        if (obj['class'] === 'widget_menu') {
          html = data.content;
        } else {
          // дефолтное построение
          const template = this.templatesService.getCompiledTemplate('default')!;
          if (template) {
            html = template(data);
          } else {
            this.logger.error(`[UiService] 'default' template is missing in templates registry!`);
          }
        }

        if (obj.to) {
          $(obj.to).append(html);
        } else {
          if (this.buffering) {
            this.buffer += html;
          } else {
            const config = this.configService.config;
            if (obj.virtual_render && config.virtual_render) {
              return html;
            } else {
              $('#container').append(html);
            }
          }
        }
      }

      return;
    }

    if (!obj || typeof obj !== 'object') {
      // @ts-expect-error Impossible situation, but it was in legacy code.
      obj = {};
    }

    const id: string = obj.id || Math.random().toString(36).slice(2);
    let tmp: JQuery<HTMLElement> = $('#' + id);

    if (tmp.length) {
      this.logger.warn(`[UiService.build_page] page #${id} already exists`);
    } else {
      tmp = $('#sample_page').clone().hide();
      tmp.attr('id', id);
      if (obj.className) {
        tmp.addClass(obj.className);
      }
      $('#container').append(tmp);
    }

    const backHtml =
      obj.back && obj.back.href
        ? `<div class="back" href="${obj.back.href}" onvclick="${obj.back.onvclick}" href_type="back"></div>`
        : `<div class="back" onvclick="navigate('#menu')" href_type="back"></div>`;
    const headerHml = `${backHtml}<h1>${obj.title ? obj.title : 'Default'}</h1>`;

    tmp.find('.header').html(headerHml);
    tmp.find('.content').html(obj.content || '');

    return id;
  }

  public pay_page(obj: {
    back?: {
      href: string;
      onvclick: string;
    };
    type: string;
    amount: number;
    text: string;
    check: string | number;
    confirm_text: string;
    data: any;
    onSuccess: () => void;
  }) {
    //type - type of pay (ex. vod)
    //amount - amount to pay
    //check - text to check
    //text - text to display
    //confirm_text - text to display in confirm window
    //data - payload (ex. video_id)

    //???? image

    // TODO: use lang

    // TODO refactor window.currency_format
    // TODO extract accounting methods to separate service to isolate framework
    const price = accounting.formatMoney(obj.amount || 0, window.currency_format);

    const tmp = {
      id: 'pay_page',
      back: obj.back,
      title: 'Pay Page',
      content: `
<div>
  ${obj.text}
  <p>POS: ${obj.type}</p>
  <p>Amount to pay: <span class="price">${price}</span></p>
  <div id="pay_button" clickable class="button" onvclick="">Pay</div>
</div>
`,
    };

    //TODO: send request

    // todo: refactor window.navigate, window.event_link, window.custom_input, window.custom_alert
    window.navigate('#' + this.build_page(tmp));

    $('#pay_button').on(window.event_link, () => {
      window.custom_input({
        text: obj.confirm_text || '',
        check: obj.check,
        onConfirm: () => {
          //TODO: make request

          const apiUrl = this.urlsService.apiUrl$.getValue();
          post<{result: number}>({
            url: `${apiUrl}addPayContent`,
            data: {
              type: obj.type,
              data: obj.data,
              token: storage.getItem('token'),
            },
          })
            .then((response) => {
              //TODO: use lang
              if (response?.result === 0) {
                try {
                  if (obj.onSuccess && typeof obj.onSuccess === 'function') {
                    obj.onSuccess();
                  }
                } catch (err) {
                  this.logger.critical('[UiService.pay_page] Unable to execute onSuccess callback', err);
                }
              } else {
                this.logger.log('[UiService.pay_page] Payment error', response);
                window.custom_alert('Pay error: ' + response?.result);
              }
            })
            .catch((err) => {
              this.logger.critical(`[UiService.pay_page] Unexpected error during addPayContent API request`, err);
            });
        },
      });
    });
  }

  /**
   * Method to build popup page.
   * @deprecated It's recommended to use Popup.show and Popup.hide
   * @param data
   * @param firstInit
   */
  public popup_list_page(
    data: {
      tplType: string;
      to?: string | null;
    },
    firstInit: boolean,
  ) {
    const tmpl = this.templatesService.getCompiledTemplate(data.tplType)!;
    const html = tmpl.render(data);

    let to: undefined | JQuery<HTMLElement> = undefined;
    if (data.to) {
      to = $(data.to);
      if (to.length && firstInit) {
        to.remove();
        to = undefined;
      }
    }

    if (to && to.length) {
      to.append(html);
    } else {
      $('#container').append(html);
    }
  }

  /**
   * Render page
   * @param id Id of the page being created
   * @param data_obj data object
   * @param tplType template name
   * @param pageClassName page class (will be added)
   * @param toAdd where to add the page (selector)
   * @param func Dictionary of functions used in the template
   */
  public render(
    id: string,
    data_obj: any,
    tplType: string,
    pageClassName?: string,
    toAdd?: string,
    func?: Record<string, (...args: any[]) => any>,
  ) {
    const config = this.configService.config;

    if (config.virtual_render) {
      const registeredPage = this._pages[data_obj?.id || id];
      if (!registeredPage) {
        this.logger.error(`[UiService.render] Unable to find registered page ${data_obj.id}`);
        return;
      }

      //TODO: сомнительно, не работает на динамических страницах
      registeredPage.content = this.renderPageOnTheStructv2(id, data_obj, tplType, pageClassName, toAdd, func, true);
    } else {
      this.renderPageOnTheStructv2(id, data_obj, tplType, pageClassName, toAdd, func);
    }
  }

  /**
   * @param {string} id - id создаваемой страницы
   * @param {object} data_obj - объект с данными
   * @param {string} tplType - имя шаблона
   * @param {string} [klass] - класс страницы (добавляется)
   * @param {string} [toAdd] - куда добавляем страницу
   * @param {object} [func] - словарь ф-ий использующихся в шаблоне
   * @param {boolean} [virtual_render]
   * */
  public renderPageOnTheStructv2(
    id: string | null,
    data_obj: any,
    tplType: string,
    klass?: string,
    toAdd?: string | Element,
    func?: Record<string, (...args: any[]) => any>,
    virtual_render?: boolean,
  ): string | undefined {
    // TODO finish refactor this function!

    const screenSize = this.screenSizeService.screenSize;
    const config = this.configService.config;
    const structv2 = this.structService.structure$.getValue()!;

    const ph = screenSize.wh - screenSize.hh - (config.menu ? 86 : 126); //TODO: поправить цифры для скандика, похоже
    // используется только в галерее

    const data: typeof data_obj & {
      parentTitle?: string;
      metro_menu?: boolean;
      scandic_menu?: boolean;
      imageListArrayObject?: {count: number; src: string}[];
      galleryContainerWidth?: number;
    } = {
      ...data_obj,
    };

    // не рендерим страницы, если нет в конфиге
    if ((tplType === 'cart' && !structv2.config.cart) || (tplType === 'shop_order' && !structv2.config.cart)) {
      return;
    }

    if (id && id.indexOf('id') !== -1 && id.indexOf('guide') === -1) {
      id = id.split('_')[1];
    }

    // удаляем страницу и рендерим с нуля.
    // когда уйдем от struct.txt этот блок можно будет удалить
    if (id) {
      const page = document.getElementById(id);
      if (page) {
        $(page).remove();
        this.logger.debug(`[UiService.renderPageOnTheStructv2] page removed, id: ${id}`);
        this.legacyLogService.add('Page removed id: ' + id);
      }
    }

    if (config.menu) {
      data.metro_menu = true;
      if (config.menu === 'scandic') {
        data.scandic_menu = true;
      }
    }

    if (data.parentId) {
      const parentPageTitle = structv2.pages?.[`id_${data.parentId}`]?.title;

      if (parentPageTitle) {
        data.parentTitle = parentPageTitle;
      }
    }

    // конвертация массива imageList в массив объектов
    // и вычисление общее ширины
    // так как шаблонизатор не показывает индексы
    // и не умеет умножать
    if (tplType === 'gallery') {
      data.imageListArrayObject = (data.imageList || []).map((image, i) => ({
        count: i,
        src: image,
      }));
      data.galleryContainerWidth = window.pw * data.imageListArrayObject.length;

      // если включен этот флаг
      // при входе в галерею сразу открывается
      // полноэкранный просмотр фото
      if (data.fullScreen) {
        data.onopen = 'tv_ok();';
      }
    }

    // превращаем объект объектов в массив объектов
    if (tplType === 'guide') {
      data.categoriesArr = window.objectToArrayList(data.categories);
      data.markersArr = window.objectToArrayList(data.markers);
      for (const key in data.categories) {
        window.guide[data.categories[key].id] = [];
      }

      for (const key in data.markers) {
        const arr = [];

        arr.push(data.markers[key].title);
        arr.push(data.markers[key].lat);
        arr.push(data.markers[key].lng);
        arr.push(data.markers[key].content);

        window.guide[data.markers[key].categoryId].push(arr);
      }
    }

    if (tplType === 'menu_classic' || tplType === 'menu_scandic' || tplType === 'menu_custom') {
      data.menuArr = window.objectToArrayList(data);

      for (let j = 0; j < data.menuArr.length; j++) {
        const menu_item = data.menuArr[j];

        if (config.tv?.hacks?.do_not_use_svg) {
          // TODO это еще актуально?
          // преобразовываем иконки
          if (window.iconsV2[menu_item.icon]) {
            menu_item.icon = 'icons8-' + window.iconsV2[menu_item.icon].toLowerCase();
          } else if (menu_item.icon === '') {
            menu_item.icon = menu_item['class'].toLowerCase();
          }
          menu_item.svg_icon = false;
        } else {
          const svgModule = inject<SvgModule>('SvgModule');

          if (structv2.config?.icon_library) {
            menu_item.iconVal = svgModule.getIconSvg(menu_item.icon);
          } else {
            menu_item.iconVal = svgModule.getIconSvg(menu_item.icon) || svgModule.getIconSvg(menu_item['class']);

            // преобразовываем иконки
            if (window.iconsV2[menu_item.icon]) {
              menu_item.icon = 'icons8-' + window.iconsV2[menu_item.icon].toLowerCase();
            } else if (menu_item.icon === '') {
              menu_item.icon = menu_item['class'].toLowerCase();
            }
          }
          menu_item.svg_icon = true;
        }

        if (menu_item.type === 'widget' && window.widgetObserver) {
          window.widgetObserver.add(menu_item.id, menu_item);
        }
      }
    }

    if (tplType === 'cart' || tplType === 'shopCategory') {
      data.symbol = structv2.config?.currency?.symbol;
    }

    if (tplType === 'language') {
      //data.language = storage.getItem('language') ? storage.getItem('language') :
      // isset('structv2.config.defaults.language');
      data.language = window.get_language();
    }

    // перевод TODO: проверить новое построение
    data.lang = window.getObjectLang();

    data.width = window.pw;
    data.height = ph;
    data.hh = window.hh;

    data.new_preload = config.preload_images == 3;

    const tmpl = this.templatesService.getCompiledTemplate(tplType)!;
    const html = tmpl.render(
      data,
      Object.assign(
        {
          isset: window.isset,
          getPageType: window.getPageType,
          getPageFromStruct: window.getPageFromStruct,
          isDynamicQr: window.isDynamicQr,
          showQrCode: window.showQrCode,
        },
        func ? func : {},
      ),
    );

    return UI.build_page({
      id: id,
      content: html,
      tpl: true,
      tplType: tplType,
      class: klass,
      width: window.pw,
      height: ph,
      to: toAdd,
      onopen: data.onopen ? data.onopen : null,
      onclose: data.onclose ? data.onclose : null,
      devices: data.devices && data.devices.length ? data.devices.toString() : null,
      groups: data.groups && data.groups.length ? data.groups.toString() : null,
      virtual_render: virtual_render,
    });
  }

  /**
   * Loads and renders a page based on the structv2 template system
   * @param {string} template_url - URL of the template to load
   * @param {string} css_url - URL of the CSS file to load (optional)
   * @param {object} data_obj - data object
   * @param {string} id - id of the created page
   * @param {string} [klass] - page class (added)
   * @param {string} [toAdd] - where to add the page
   * @param {object} [func] - dictionary of functions used in template
   * @param {boolean} virtual_render - Whether to perform virtual rendering
   * @returns {Promise} Promise that resolves when page is loaded and rendered
   */
  public async loadAndRenderPageOnTheStructv2(
    template_url: string,
    css_url: string | null,
    data_obj: any,
    id: string | null,
    klass?: string,
    toAdd?: string,
    func?: Record<string, (...args: any[]) => any>,
    virtual_render?: boolean,
  ) {
    const url_split = template_url.split('/');
    const tplType = url_split[url_split.length - 1].split('.')[0];

    if (!id) {
      id = tplType;
    }

    if (!klass) {
      klass = tplType;
    }

    if (css_url) {
      // Ensure adding only one CSS file for one template
      window.css_append(css_url, tplType + '_css');
    }

    await this.templatesService.loadTemplate(template_url);

    this.renderPageOnTheStructv2(id, data_obj, tplType, klass, toAdd, func, virtual_render);
  }

  private bufferingStart() {
    this.buffering = true;
    this.buffer = '';
  }

  private bufferingStop() {
    $('#container').append(this.buffer);
    this.buffer = '';
    this.buffering = false;
  }

  private renderFullScreenOverlay() {
    const template = this.templatesService.getCompiledTemplate('fullscreen_overlay')!;
    const renderedTemplate = template.render({}, {getlang: window.getlang});
    $(document.body).append($(renderedTemplate));
    // $(document.body).append($(templates_cache['fullscreen_overlay'].render({}, {getlang: getlang})));
  }

  private preRenderPages() {
    // построение страниц по struct
    const structv2 = this.structService.structure$.getValue()!;
    const config = this.configService.config;

    // рендер legacy welcome'a. Уже нигде не используется. Но пока просто закоментирую.
    // this.renderPageOnTheStructv2('welcome', structv2.welcome, 'welcome');

    // путеводитель
    this.renderPageOnTheStructv2(
      'guide',
      {
        title: this.i18nService.translate('mobileAppContent-mainMenuPage-label-guide'),
        backBtn: 0,
        ...structv2.guide,
      },
      'guide',
    );

    // корзина
    // this.renderPageOnTheStructv2('cart', structv2.cart, 'cart');
    // this.renderPageOnTheStructv2('shop_order', {}, 'shop_order', 'white');

    // настройки
    this.renderPageOnTheStructv2(
      'settings',
      {
        title: this.i18nService.translate('settings'),
        parental_lock: config.tv?.parental_lock,
        sleep_timer: config.tv?.sleep_timer?.enabled,
        backBtn: 0,
      },
      'settings',
    );

    // счет строится в viewbill

    this.renderPageOnTheStructv2(
      'language_select',
      {
        languageArr: structv2.language,
        title: this.i18nService.translate('mobileAppContent-mainContent-settings-chooselang'),
        backBtn: 1,
        parentId: 'settings',
      },
      'language',
    );

    // обратная связь
    this.renderPageOnTheStructv2(
      'feedback',
      {
        title: this.i18nService.translate('mobileAppContent-mainMenuPage-label-feedback'),
        stars: [1, 2, 3, 4, 5],
        backBtn: 0,
        ...structv2.feedback,
      },
      'feedback',
      'new',
    );

    // генерация sample_page для логов
    this.renderPageOnTheStructv2('sample_page', {title: 'Лог'}, 'sample_page', 'white');

    this.renderPageOnTheStructv2('service_page', {title: 'Service'}, 'service_page_for_data');
    $('#service_resolution').html(window.ww + 'x' + window.wh);

    if (structv2.config.demo) {
      this.renderPageOnTheStructv2(
        'room_control',
        {
          title: this.i18nService.translate('mobileAppContent-mainMenuPage-label-roomcontrol'),
          backBtn: 0,
        },
        'room_control_demo',
      );
      this.renderPageOnTheStructv2(
        'tv_control',
        {
          title: this.i18nService.translate('mobileAppContent-mainMenuPage-label-tvcontrol'),
          backBtn: 0,
        },
        'tv_control_demo',
      );
    }

    for (const i in structv2.pages) {
      const page = structv2.pages[i];

      if (page.type === 'authQR') {
        this.renderPageOnTheStructv2(
          i,
          {
            ...page,
            onopen: `QR.get('${page.id}', 'page')`,
          },
          'auth-qr',
          'white',
        );
      }

      if (page.type === 'video') {
        this.renderPageOnTheStructv2(
          i,
          {
            ...page,
            onopen: 'fullscreenVideoPage.open();',
            onclose: 'fullscreenVideoPage.close();',
          },
          'fullscreen_video',
          'fullscreen_video',
        );
      }

      if (page.type === 'gallery') {
        this.renderPageOnTheStructv2(i, page, 'gallery', 'gallery_page');
      }
    }
  }
}

declare global {
  interface Window {
    UI: UiService;
    renderPageOnTheStructv2: UiService['renderPageOnTheStructv2'];
    loadAndRenderPageOnTheStructv2: UiService['loadAndRenderPageOnTheStructv2'];
  }
  const UI: UiService;
  const renderPageOnTheStructv2: UiService['renderPageOnTheStructv2'];
  const loadAndRenderPageOnTheStructv2: UiService['loadAndRenderPageOnTheStructv2'];
}

interface UiPageRegistryEntry {
  // id: string;
  images?: string[];
  action?: {func: (...args: any[]) => void; param?: any};
  content?: string;
}
